import React from 'react'
import { useCallback, useMemo, useReducer, useRef, useState } from 'react'
import { Maybe } from '@graphql-tools/utils'
import { useToast } from '@/molecules/Toast'
import { useAngelPlayerContext } from '@/organisms/AngelPlayer'
import { usePlayerMoments } from '@/organisms/CollectiblesPlayer'
import { Moment, UserReaction } from '@/types/codegen-federation'
import { ReactFCC } from '@/types/react'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { reduceReactsPerMoment, TAPS_BEFORE_TELL_YOU_PROMPT } from './util'

interface CollectiblesPlayerContextValues {
  getCanPromptWhyTheyLoveThis: () => boolean
  getCurrentMoment: () => Maybe<Moment>
  getCurrentMomentReactionCount: () => number
  onMomentChanged: (moment: Moment) => void
  toggleShowReactionButton: () => void
  isShowingReactionButton: boolean
  saveReaction: (reaction: UserReaction) => void
}

export const CollectiblesPlayerContext = React.createContext<CollectiblesPlayerContextValues>({
  getCanPromptWhyTheyLoveThis: () => false,
  getCurrentMoment: () => null,
  getCurrentMomentReactionCount: () => 0,
  onMomentChanged: () => undefined,
  toggleShowReactionButton: () => undefined,
  isShowingReactionButton: false,
  saveReaction: () => undefined,
})

export const CollectiblesPlayerProvider: ReactFCC = ({ children }) => {
  const { controller, uiManager } = useAngelPlayerContext()
  const { getMoment, canDisplayScenes } = usePlayerMoments()

  const track = useSafeTrack()
  const [reactionsPerMoment, updateReactionsPerMoment] = useReducer(reduceReactsPerMoment, {})
  const [canShowReactionButton, setCanShowReactionButton] = useState(true)
  const reactionsToSaveRef = useRef<UserReaction[]>([])
  const { showToast } = useToast()
  const { t } = useTranslate('watch')

  const getCurrentMoment = useCallback(() => {
    const currentTime = controller.getCurrentTime()
    return getMoment({ time: currentTime })
  }, [controller, getMoment])

  const getCurrentMomentReactionCount = useCallback(() => {
    return reactionsPerMoment[getCurrentMoment()?.id || '']
  }, [reactionsPerMoment, getCurrentMoment])

  const getCanPromptWhyTheyLoveThis = useCallback((): boolean => {
    const currentMoment = getCurrentMoment()
    const currentMomentReactionsCount = getCurrentMomentReactionCount()

    return !!currentMoment && currentMomentReactionsCount >= TAPS_BEFORE_TELL_YOU_PROMPT && !currentMoment.hasStory
  }, [getCurrentMoment, getCurrentMomentReactionCount])

  const onMomentChanged = useCallback(
    (moment: Moment) => {
      track('Moment Clicked', {
        momentId: moment.id,
        momentStartTime: moment.startTime,
        momentTitle: moment.title,
      })

      if (!controller?.isPlaying()) {
        controller?.play()
      }
    },
    [controller, track],
  )

  const toggleShowReactionButton = useCallback(() => {
    setCanShowReactionButton((current) => {
      const isShowing = !current

      if (isShowing) {
        uiManager?.showTimelineMarkers()
      } else {
        uiManager?.hideTimelineMarkers()
      }

      const currentMoment = getCurrentMoment()

      track('Show Reaction Clicked', {
        momentId: currentMoment?.id,
        momentTitle: currentMoment?.title,
        isShowingReactionButton: isShowing,
      })

      return isShowing
    })
  }, [getCurrentMoment, setCanShowReactionButton, track, uiManager])

  const saveReaction = useCallback(
    (reaction: UserReaction) => {
      if (!reaction.momentId) {
        return
      }

      reactionsToSaveRef.current = [...reactionsToSaveRef.current, reaction] as UserReaction[]
      updateReactionsPerMoment({ momentId: reaction.momentId })

      const currentMoment = getCurrentMoment()

      if (currentMoment?.title) {
        showToast(t('addedToLovedScenes', `"{{title}}" added to my Loved Scenes`, { title: currentMoment.title }), {
          canRepeatToasts: false,
          shouldHideClose: true,
        })
      }
    },
    [getCurrentMoment, showToast, t],
  )

  const value = useMemo(() => {
    const isShowingReactionButton = canDisplayScenes && canShowReactionButton

    return {
      getCanPromptWhyTheyLoveThis,
      getCurrentMoment,
      getCurrentMomentReactionCount,
      onMomentChanged,
      toggleShowReactionButton,
      isShowingReactionButton,
      saveReaction,
    }
  }, [
    canDisplayScenes,
    canShowReactionButton,
    getCanPromptWhyTheyLoveThis,
    getCurrentMoment,
    getCurrentMomentReactionCount,
    onMomentChanged,
    toggleShowReactionButton,
    saveReaction,
  ])

  return <CollectiblesPlayerContext.Provider value={value}>{children}</CollectiblesPlayerContext.Provider>
}

export const useCollectiblesPlayer = () => {
  return React.useContext(CollectiblesPlayerContext)
}
